import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Lightweight hook that only tracks the coach chat unread count.
 * Uses React Query for caching + Supabase realtime for live updates.
 */
export function useCoachUnread() {
  const { session } = useAuth();
  const user = session?.user;
  const queryClient = useQueryClient();

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['coach-unread', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('expert_chat_conversations')
        .select('unread_by_user')
        .eq('user_id', user!.id)
        .maybeSingle();

      return data?.unread_by_user || 0;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // Realtime subscription updates the query cache directly
  useEffect(() => {
    if (!user?.id) return;

    const subscription = supabase
      .channel('coach-unread')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'expert_chat_conversations',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        const updated = payload.new as { unread_by_user: number };
        queryClient.setQueryData(['coach-unread', user.id], updated.unread_by_user || 0);
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id, queryClient]);

  return unreadCount;
}
